'use client';

import { useEffect } from 'react';
import { Inbox } from 'lucide-react';
import { ModalShell } from '@/components/ui/ModalShell';
import { MisSolicitudesTab } from '@/components/investigador/tabs/MisSolicitudesTab';

/**
 * "Mis solicitudes" como modal (R62). Dejó de ser una pestaña del perfil cuando
 * las secciones del dueño subieron a la barra de acciones: abrir dentro del
 * cuerpo del perfil una sección que se dispara desde arriba obligaba a bajar la
 * vista para ver el resultado del clic.
 *
 * El contenido es el MISMO componente de siempre; acá sólo cambia el marco.
 *
 * SIN alto fijo a propósito: `minHeight` de `ModalShell` fija también el `height`,
 * y con pocas solicitudes —o ninguna— el listado quedaba flotando en medio de una
 * tarjeta vacía. Sin él la tarjeta crece con el contenido hasta el tope de 90vh,
 * que es como se comportaba cuando esto era una pestaña.
 *
 * Con `slug` es el MISMO listado, pero el de otra persona: lo que ve un
 * administrador desde el perfil ajeno (R63). Historial y nada más — la vista
 * ajena no crea solicitudes (ver `MisSolicitudesTab`).
 */
interface MisSolicitudesModalProps {
	onClose: () => void;
	/** Perfil ajeno cuyo historial se muestra. Ausente = las del usuario en sesión. */
	slug?: string;
	/** Nombre visible del dueño de ese perfil, para titular la vista ajena. */
	nombre?: string | null;
}

export function MisSolicitudesModal({ onClose, slug, nombre }: MisSolicitudesModalProps) {
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [onClose]);

	return (
		<ModalShell
			title={slug ? `Solicitudes de ${nombre ?? 'la persona'}` : 'Mis solicitudes'}
			icon={<Inbox size={16} />}
			onClose={onClose}
			onCancel={onClose}
			cancelText="Cerrar"
			cancelTone="brand"
			showSaveAction={false}
			hideFooterIndicator
			maxWidth="58rem"
		>
			<MisSolicitudesTab slug={slug} />
		</ModalShell>
	);
}
